/**
 * Criteo Adapter
 *
 * Adapts standardized event payloads into Criteo OneTag events.
 *
 * Each adapted event is the exact object that gets pushed to the
 * window.criteo_q queue. The Pixel Manager sends the shopping journey
 * events Criteo uses for retargeting and audience building: viewItem on
 * product pages, viewList on product listings (collected and sent as one
 * event by the event listeners), addToCart, viewBasket on the cart page
 * and trackTransaction on the order confirmation page.
 *
 * @see https://help.criteo.com/kb/guide/en/introduction-to-the-criteo-onetag-8fjCDwCENw
 */

(function (pmw, undefined) {

	class CriteoAdapter {

		/**
		 * Adapt an event for Criteo
		 *
		 * @param {Object} eventData - Standardized event data
		 * @param {string} eventName - Standardized event name
		 * @param {Object} context - Additional context
		 * @returns {Object|null} - Criteo formatted event or null to skip
		 */
		adapt(eventData, eventName, context) {
			const methodName = `adapt${this.toPascalCase(eventName)}`;

			if (typeof this[methodName] === "function") {
				return this[methodName](eventData, context);
			}

			// Criteo does not track every standardized event - skip the rest.
			return null;
		}

		toPascalCase(str) {
			if (typeof str !== "string") return "";
			return str.replace(/(^|_)(\w)/g, (_, __, c) => c.toUpperCase());
		}

		/**
		 * Get the product identifier for Criteo
		 *
		 * Uses the configured dynamic remarketing ID type so the IDs match
		 * the product feed submitted to Criteo.
		 */
		getProductId(product) {
			if (!product) return null;

			const idType = pmwDataLayer?.pixels?.criteo?.dynamic_remarketing?.id_type;

			if (idType && product.dyn_r_ids && product.dyn_r_ids[idType]) {
				return String(product.dyn_r_ids[idType]);
			}

			return product.id ? String(product.id) : null;
		}

		/**
		 * Round a value to two decimals; Criteo expects a numeric price.
		 */
		toPrice(value) {
			const price = Math.round(Number(value) * 100) / 100;
			return Number.isFinite(price) ? price : 0;
		}

		/**
		 * Adapt ViewItem event
		 *
		 * Criteo's "viewItem" event marks a product detail page view.
		 */
		adaptViewItem(eventData, context) {
			const id = this.getProductId(eventData.product);
			if (!id) return null;

			return {
				event: "viewItem",
				item : id,
			};
		}

		/**
		 * Adapt ViewItemList event
		 *
		 * Criteo's "viewList" event carries the product IDs of a listing page.
		 * The Pixel Manager emits one view_item_list event per visible product,
		 * so the adapter returns a single-item viewList and the event listener
		 * collects them into one viewList event per page.
		 */
		adaptViewItemList(eventData, context) {
			const id = this.getProductId(eventData.product);
			if (!id) return null;

			return {
				event: "viewList",
				item : [id],
			};
		}

		/**
		 * Adapt AddToCart event
		 */
		adaptAddToCart(eventData, context) {
			const product = eventData.product;
			const id      = this.getProductId(product);
			if (!id) return null;

			return {
				event: "addToCart",
				item : [
					{
						id      : id,
						price   : this.toPrice(product.price),
						quantity: Number(product.quantity) || 1,
					},
				],
			};
		}

		/**
		 * Adapt ViewCart event
		 *
		 * Criteo's "viewBasket" event carries all products in the cart.
		 */
		adaptViewCart(eventData, context) {
			if (!pmwDataLayer?.cart || pmw.isEmptyObject(pmwDataLayer.cart)) return null;

			const items = [];

			Object.values(pmwDataLayer.cart).forEach(product => {
				const id = this.getProductId(product);
				if (!id) return;

				items.push({
					id      : id,
					price   : this.toPrice(product.price),
					quantity: Number(product.quantity) || 1,
				});
			});

			if (!items.length) return null;

			return {
				event: "viewBasket",
				item : items,
			};
		}

		/**
		 * Adapt Purchase event
		 *
		 * Criteo's "trackTransaction" event carries the order ID and all
		 * purchased products.
		 */
		adaptPurchase(eventData, context) {
			if (!pmwDataLayer?.order) return null;

			const items = [];

			Object.values(pmwDataLayer.order.items || {}).forEach(item => {

				// Resolve the purchased variation when variations are output,
				// falling back to the parent product.
				let product = pmwDataLayer.products?.[item.id];

				if (pmwDataLayer?.shop?.variations_output && item.variation_id && 0 !== item.variation_id) {
					product = pmwDataLayer.products?.[item.variation_id] || product;
				}

				const id = this.getProductId(product) || (item.id ? String(item.id) : null);
				if (!id) return;

				items.push({
					id      : id,
					price   : this.toPrice(item.price),
					quantity: Number(item.quantity) || 1,
				});
			});

			if (!items.length) return null;

			return {
				event: "trackTransaction",
				id   : String(pmwDataLayer.order.number || pmwDataLayer.order.id),
				item : items,
			};
		}
	}

	// Register the adapter with capabilities
	const adapter = new CriteoAdapter();
	pmw.registerAdapter("criteo", adapter, {
		supportsS2S               : false,
		supportsEventDeduplication: false,
		isActive                  : () => pmwDataLayer?.pixels?.criteo?.account_id ? true : false,
	});

}(window.pmw = window.pmw || {}));
